import { ATGPayAuctionSuccessPayload, ATGPayInvoicePayload, RecalculateInvoicesPayload } from './atgPayAuction.types';
import { getCookieByName } from '@/utils/cookies';
import { handleResponse, makeGet, makePost } from '@/redux/api/helpers';

const token = getCookieByName('auctioneer-auth');

type GenerateJWTParams = {
    deployment: string;
    supportId: number;
};

type GenerateJWTResponse =
    | {
          error: true;
          payload: string;
      }
    | {
          error: false;
          payload: { token: string };
      };

// JWT token cache with 5-minute TTL, keyed by marketplace code
const cachedJWTTokens = new Map<string, string>();
const cachedJWTExpirations = new Map<string, number>();

const isJWTCacheValid = (marketplaceCode: string): boolean => {
    const cacheKey = marketplaceCode.toLowerCase();
    const cachedToken = cachedJWTTokens.get(cacheKey);
    const cachedExpiration = cachedJWTExpirations.get(cacheKey);
    if (!cachedToken || !cachedExpiration) {
        return false;
    }
    const now = Date.now();
    return now < cachedExpiration;
};

const generateJWT = ({ deployment, marketplaceCode, supportId }: GenerateJWTParams & { marketplaceCode: string }) =>
    new Promise<GenerateJWTResponse>((resolve) => {
        const queryParams: Record<string, string> = { platformCode: 'LA' };
        if (marketplaceCode !== 'LA') {
            queryParams.marketplaceCode = marketplaceCode;
        }
        const request = makePost({
            apiPath: 'platform/generate-jwt',
            authToken: token,
            deployment,
            path: '<ATGPAY-MARKETPLACE-AUTH-API>',
            queryParams,
        });

        // Set token to expire in 1 day instead of 10 minutes
        const expiration = new Date();
        expiration.setDate(expiration.getDate() + 1);
        const exp = Math.floor(expiration.getTime() / 1000);

        const aud = `${marketplaceCode.toLowerCase()}/support`;

        request.send({
            aud: [aud],
            exp,
            sub: `${supportId}`,
        });

        request.end((err, response) => {
            if (err) {
                return resolve({ error: true, payload: err.message || 'Failed to generate JWT' });
            }
            const body = response?.body;
            if (body && !body.error && body.payload) {
                return resolve({ error: false, payload: { token: body.payload } });
            }
            if (body && body.error) {
                return resolve({ error: true, payload: body.payload || 'JWT generation failed' });
            }
            return resolve({ error: true, payload: 'Invalid JWT response structure' });
        });
    });

export const getOrGenerateJWT = async ({
    deployment,
    marketplaceCode,
    supportId,
}: GenerateJWTParams & { marketplaceCode: string }): Promise<GenerateJWTResponse> => {
    const cacheKey = marketplaceCode.toLowerCase();

    // Return cached token if still valid
    if (isJWTCacheValid(marketplaceCode)) {
        const cachedToken = cachedJWTTokens.get(cacheKey);
        if (cachedToken) {
            return { error: false, payload: { token: cachedToken } };
        }
    }

    // Generate new token
    const jwtResponse = await generateJWT({ deployment, marketplaceCode, supportId });

    // Cache the new token with 5-minute TTL
    if (!jwtResponse.error) {
        const token = (jwtResponse.payload as { token: string }).token;
        cachedJWTTokens.set(cacheKey, token);
        cachedJWTExpirations.set(cacheKey, Date.now() + 5 * 60 * 1000); // 5 minutes from now
    }

    return jwtResponse;
};

type GetATGPayAuctionParams = {
    catalogId: string;
    deployment: string;
    marketplaceCode: string;
    supportId: number;
};

export type GetATGPayAuctionResponse =
    | {
          error: true;
          payload: string;
      }
    | {
          error: false;
          payload: ATGPayAuctionSuccessPayload;
      };

export const getATGPayAuction = async ({
    catalogId,
    deployment,
    marketplaceCode,
    supportId,
}: GetATGPayAuctionParams): Promise<GetATGPayAuctionResponse> => {
    // Get cached JWT token or generate a new one if expired
    const jwtResponse = await getOrGenerateJWT({ deployment, marketplaceCode, supportId });
    if (jwtResponse.error) {
        return { error: true, payload: jwtResponse.payload };
    }

    const jwtToken = (jwtResponse.payload as { token: string }).token;

    // Then use the JWT token for the actual API call
    return new Promise<GetATGPayAuctionResponse>((resolve, reject) => {
        const queryParams: Record<string, string> = { platformCode: 'LA' };
        if (marketplaceCode !== 'LA') {
            queryParams.marketplaceCode = marketplaceCode;
        }
        const request = makeGet({
            apiPath: `support/${supportId}/auctions/${catalogId}`,
            authToken: jwtToken,
            deployment,
            path: '<ATGPAY-MARKETPLACE-AUTH-API>',
            queryParams,
        });

        request.end((err, response) => handleResponse({ err, reject, resolve, response }));
    });
};

type RecalculateInvoicesParams = {
    catalogId: string;
    deployment: string;
    marketplaceCode: string;
    supportId: number;
};

export type RecalculateInvoicesResponse =
    | {
          error: true;
          payload: string;
      }
    | {
          error: false;
          payload: RecalculateInvoicesPayload;
      };

export const recalculateInvoices = async ({
    catalogId,
    deployment,
    marketplaceCode,
    supportId,
}: RecalculateInvoicesParams): Promise<RecalculateInvoicesResponse> => {
    // Get cached JWT token or generate a new one if expired
    const jwtResponse = await getOrGenerateJWT({ deployment, marketplaceCode, supportId });
    if (jwtResponse.error) {
        return { error: true, payload: jwtResponse.payload };
    }

    const jwtToken = (jwtResponse.payload as { token: string }).token;

    // Then use the JWT token for the actual API call
    return new Promise<RecalculateInvoicesResponse>((resolve, reject) => {
        const queryParams: Record<string, string> = { platformCode: 'LA' };
        if (marketplaceCode !== 'LA') {
            queryParams.marketplaceCode = marketplaceCode;
        }
        const request = makePost({
            apiPath: `support/${supportId}/auctions/${catalogId}/recalculate`,
            authToken: jwtToken,
            deployment,
            path: '<ATGPAY-MARKETPLACE-AUTH-API>',
            queryParams,
        });

        request.end((err, response) => handleResponse({ err, reject, resolve, response }));
    });
};

type GetATGPayInvoiceParams = {
    deployment: string;
    invoiceId: string;
    marketplaceCode: string;
    supportId: number;
};

export type GetATGPayInvoiceResponse =
    | {
          error: true;
          payload: string;
      }
    | {
          error: false;
          payload: ATGPayInvoicePayload;
      };

export const getATGPayInvoice = async ({
    deployment,
    invoiceId,
    marketplaceCode,
    supportId,
}: GetATGPayInvoiceParams): Promise<GetATGPayInvoiceResponse> => {
    // Get cached JWT token or generate a new one if expired
    const jwtResponse = await getOrGenerateJWT({ deployment, marketplaceCode, supportId });
    if (jwtResponse.error) {
        return { error: true, payload: jwtResponse.payload };
    }

    const jwtToken = (jwtResponse.payload as { token: string }).token;

    // Fetch the invoice details
    return new Promise<GetATGPayInvoiceResponse>((resolve, reject) => {
        const queryParams: Record<string, string> = { platformCode: 'LA' };
        if (marketplaceCode !== 'LA') {
            queryParams.marketplaceCode = marketplaceCode;
        }
        const request = makeGet({
            apiPath: `support/${supportId}/invoices/${invoiceId}`,
            authToken: jwtToken,
            deployment,
            path: '<ATGPAY-MARKETPLACE-AUTH-API>',
            queryParams,
        });

        request.end((err, response) => handleResponse({ err, reject, resolve, response }));
    });
};

export const getATGPayV2Invoice = async ({
    deployment,
    invoiceId,
    marketplaceCode,
    supportId,
}: GetATGPayInvoiceParams): Promise<GetATGPayInvoiceResponse> => {
    // Get cached JWT token or generate a new one if expired
    const jwtResponse = await getOrGenerateJWT({ deployment, marketplaceCode, supportId });
    if (jwtResponse.error) {
        return { error: true, payload: jwtResponse.payload };
    }

    const jwtToken = (jwtResponse.payload as { token: string }).token;

    // Fetch the invoice details using headers endpoint
    return new Promise<GetATGPayInvoiceResponse>((resolve, reject) => {
        const queryParams: Record<string, string> = { platformCode: 'LA' };
        if (marketplaceCode !== 'LA') {
            queryParams.marketplaceCode = marketplaceCode;
        }
        const request = makeGet({
            apiPath: `support/${supportId}/headers`,
            authToken: jwtToken,
            deployment,
            path: '<ATGPAY-MARKETPLACE-AUTH-API>',
            queryParams,
        });

        request.set('Service', 'sales-order');
        request.set('Path', `v2/marketplaces/${marketplaceCode}/invoices/${invoiceId}`);
        request.set('Debug', 'true');

        request.end((err, response) => handleResponse({ err, reject, resolve, response }));
    });
};
